/**
 * Dana's five demo days, laid out by hand — the single source for the days Act 3
 * opens on. `generateSql.ts` places these slots before the greedy fill, so the
 * seeded tenant carries exactly this shape; the store reads the result back from
 * the server rather than holding a second copy.
 *
 * The days are **working-day offsets from demo day**, not fixed weekdays: −2 and
 * −1 are the two working days behind, 0 is today, +1 and +2 the two working days
 * ahead. A Wednesday rehearsal therefore reads Monday to Friday of one week, and
 * a Friday rehearsal reads Wednesday, Thursday, Friday, then Monday and Tuesday
 * of the week after — which is what lets the acts run on any Mon–Fri.
 *
 * A slot with a `ref` is that cast patient's visit. A slot without one is filled
 * from the North skilled-nursing routine visits the plan already puts on Dana, in
 * date order, so the week is real work rather than fixture visits. A slot only
 * takes a patient whose hours it fits (`seed/schedule.ts`).
 *
 * What each day is for (Phase B, 2026-10-12):
 *  - **today** runs to a visit at 16:00, so the phone in Act 4 has a visit ahead
 *    of the clock at any hour up to then;
 *  - **the next working day** is the text round's (3.3): five visits from 09:00,
 *    and the afternoon left free, so SOC Options offers Dana a start of care
 *    inside Alvarez's 48 hours on every weekday — on a Thursday seed this Friday
 *    is the only working day in that window;
 *  - **the day after** is the call-out day (5.2): 10:00–13:30 kept for the start
 *    of care the 1.3 slip moves Alvarez's hold onto, then a working afternoon:
 *    Osei at 13:30 and two after him. The morning stays clear before 10:00 too;
 *    a 09:00 visit there cost the slip its fit on a Thursday seed. It used to keep the
 *    afternoon light for Ferreira's wound-VAC visit when Marcus calls out, which
 *    left the day nearly empty on My week; Devon is on shift that day with
 *    nothing booked and carries wound VAC too, so the room is his (Pete,
 *    2026-10-12).
 *
 * Read the week's points off the generator's calibration block: the five days
 * fall inside one calendar week only when demo day is a Wednesday.
 */
import type { VisitTypeKind } from "../state";

export interface DanaSlot {
  /** Cast patient ref, or absent for a routine visit taken from the plan. */
  ref?: string;
  kind: VisitTypeKind;
  /** "HH:mm" branch-zone wall clock. */
  start: string;
}

export interface DanaDay {
  /** Working days from demo day: −2 … +2. */
  dayOffset: number;
  slots: DanaSlot[];
}

/** The five working days around demo day; the documentation block holds 08:00–09:00. */
export const DANA_WEEK: DanaDay[] = [
  {
    dayOffset: -2,
    slots: [
      { ref: "census-01", kind: "SOC", start: "10:00" },
      { kind: "SN_ROUTINE", start: "12:45" },
      { kind: "SN_ROUTINE", start: "13:45" },
      { kind: "SN_ROUTINE", start: "14:45" },
      { kind: "SN_ROUTINE", start: "15:45" },
    ],
  },
  {
    // Whitfield first: his welcome call gave mornings, and his visit sits inside them (SEED-SCOPE 2.1).
    dayOffset: -1,
    slots: [
      { ref: "census-01", kind: "SN_ROUTINE", start: "10:00" },
      { kind: "SN_ROUTINE", start: "11:00" },
      { kind: "SN_ROUTINE", start: "12:00" },
      { kind: "SN_ROUTINE", start: "13:00" },
      { kind: "SN_ROUTINE", start: "14:00" },
    ],
  },
  {
    dayOffset: 0,
    slots: [
      { kind: "SN_ROUTINE", start: "10:00" },
      { kind: "SN_ROUTINE", start: "11:00" },
      { kind: "SN_ROUTINE", start: "12:00" },
      { kind: "SN_ROUTINE", start: "13:00" },
      { kind: "SN_ROUTINE", start: "14:00" },
      { kind: "SN_ROUTINE", start: "15:00" },
      { kind: "SN_ROUTINE", start: "16:00" },
    ],
  },
  {
    dayOffset: 1,
    slots: [
      { ref: "census-30", kind: "SN_ROUTINE", start: "09:00" },
      { ref: "census-01", kind: "SN_ROUTINE", start: "10:00" },
      { ref: "osei", kind: "SN_ROUTINE", start: "11:00" },
      { kind: "SN_ROUTINE", start: "12:00" },
      { kind: "SN_ROUTINE", start: "13:00" },
    ],
  },
  {
    dayOffset: 2,
    slots: [
      { ref: "osei", kind: "SN_ROUTINE", start: "13:30" },
      { kind: "SN_ROUTINE", start: "14:30" },
      { kind: "SN_ROUTINE", start: "15:30" },
    ],
  },
];

/**
 * The free stretch on Dana's next working day that a start of care fits into
 * (1.2): the SOC's 150 minutes plus its 30-minute drive allowance is 180, and
 * this is 180 after the round's last visit and its drive.
 */
export const DANA_NEXT_DAY_SOC_GAP = { start: "14:00", end: "17:00" };
/**
 * The free stretch the working day after that, where Alvarez's re-held start of
 * care lands (1.3). It describes the window the search actually places into: the
 * SOC's 150 minutes plus its 30-minute drive allowance is 180, and this is 210.
 */
export const DANA_DAY_AFTER_GAP = { start: "10:00", end: "13:30" };

/** Marcus keeps the next working day's morning clear for the start of care Okafor's hold converts into (1.4). */
export const MARCUS_NEXT_DAY_SOC = { start: "10:00", end: "12:30" };
